/* eslint-disable */
// @ts-nocheck

import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Step, Stepper } from '@/components/ui/stepper'
import ClientInfoStep from '@/containers/logistic/purchaseOrders/wizard/steps/ClientInfoStep'
import DeliveryDetailsStep from '@/containers/logistic/purchaseOrders/wizard/steps/DeliveryDetailsStep'
import DocumentDetailsStep from '@/containers/logistic/purchaseOrders/wizard/steps/DocumentDetailsStep'
import ProductSelectionStep from '@/containers/logistic/purchaseOrders/wizard/steps/ProductSelectionStep'
import ReviewStep from '@/containers/logistic/purchaseOrders/wizard/steps/ReviewStep'
import { Country } from '@/interfaces/billing'
import { ClientInterface } from '@/interfaces/ServerInterfaces'

export type DocumentType = 'orden-compra' | 'factura' | 'prefactura'

interface NewPurchaseOrderWizardProps {
  _open: boolean
  onOpenChange: (_open: boolean) => void
  documentType: DocumentType
  _invoiceId?: string
}

// Use existing types from the project instead of defining new ones
export interface ReceiverInfo {
  name: string
  lastName: string
  phone?: {
    number: string
    codeCountry: string
  }
  email?: string
  postalCode?: string
  notes?: string
  country?: Country
}

// Product interface for the wizard (simplified from existing types)
export interface WizardProduct {
  id: string // Can be existing ID or temp ID for new products
  productId: number // Actual product ID from backend
  name: string
  quantity: number
  priceUnitary: { amount: number; codeCurrency: string }
  totalPrice: number
  currency: string
  type?: string
  weight?: number
  volume?: number
}

// Client data for new clients (based on existing ClientInterface)
export interface NewClientData {
  name: string
  lastName?: string
  idNumber?: string
  phone?: {
    number: string
    codeCountry: string
  }
  email?: string
  contractNumber?: string
  clientType?: string
  legalNotes?: string
  mainAddress?: string
  secondaryAddress?: string
  locality?: string
  country?: { id: string; name: string }
  province?: { id: string; name: string }
  municipality?: { id: string; name: string }
  categories?: number[] // Add categories array for customer categories
  clientNotes?: string
}

// Address data for new addresses (based on existing address interfaces)
export interface NewAddressData {
  mainAddress?: string
  secondaryAddress?: string
  locality?: string
  country?: { id: string; name: string }
  province?: { id: string; name: string }
  municipality?: { id: string; name: string }
  postalCode?: string
}

// Utility functions for managing product selection keys
/**
 * Creates a compound key for product selections based on area and price system
 * @param areaId - The area/point of sale ID
 * @param priceSystemId - The price system ID
 * @returns A string key in format "areaId-priceSystemId"
 */
export const createProductSelectionKey = (
  areaId: number,
  priceSystemId: number
): string => {
  return `${areaId}-${priceSystemId}`
}

/**
 * Parses a product selection key back into its components
 * @param key - The compound key to parse
 * @returns Object with areaId and priceSystemId, or null if invalid
 */
export const parseProductSelectionKey = (
  key: string
): { areaId: number; priceSystemId: number } | null => {
  const parts = key.split('-')
  if (parts.length !== 2) return null

  const areaId = parseInt(parts[0])
  const priceSystemId = parseInt(parts[1])

  if (isNaN(areaId) || isNaN(priceSystemId)) return null

  return { areaId, priceSystemId }
}

/**
 * Gets products for a specific area and price system combination
 * @param productSelections - The product selections object
 * @param areaId - The area/point of sale ID
 * @param priceSystemId - The price system ID
 * @returns Array of products for the given combination
 */
export const getProductsForSelection = (
  productSelections: Record<string, WizardProduct[]>,
  areaId: number,
  priceSystemId: number
): WizardProduct[] => {
  const key = createProductSelectionKey(areaId, priceSystemId)
  return productSelections[key] || []
}

// Updated BillingFormData with real types
export interface BillingFormData {
  // Client Info
  clientId?: string
  isNewClient?: boolean
  newClientData?: NewClientData
  selectedClientData?: ClientInterface // Store the full selected client data for prefilling
  clientNotes?: string
  documentType: DocumentType

  // Document Details
  title?: string
  commercialId?: string
  isNewCommercial?: boolean
  paymentTerm?: string
  customDueDate?: Date
  calculatedDueDate?: Date | null
  observations?: string

  products?: WizardProduct[]

  /**
   * Products organized by area-priceSystem compound key
   * Key format: "${areaId}-${priceSystemId}"
   * Use createProductSelectionKey() to generate keys consistently
   * Use getProductsForSelection() to retrieve products safely
   */
  productSelectionsByAreaAndPriceSystem?: Record<string, WizardProduct[]>

  /** Selected area sales ID - required for invoice creation */
  selectedAreaSalesId?: number

  /** Selected price system ID - required for invoice creation */
  selectedPriceSystemId?: number

  // Delivery
  scheduleDelivery?: boolean
  receiverInfo?: ReceiverInfo
  deliveryAddressId?: string
  deliveryAddressFullAddress?: string
  isNewAddress?: boolean
  newAddressData?: NewAddressData
  deliveryPersonId?: string
  deliveryDate?: Date
  deliveryCost?: number
  deliveryCostCurrency?: string
  shippingRegionId?: number
  isImmediateDelivery?: boolean
}

// Default initial state conforming to the interface
const initialFormData: Partial<BillingFormData> = {
  newClientData: {
    name: '',
    categories: [],
  },
  selectedClientData: undefined,
  receiverInfo: { name: '', lastName: '' },
  newAddressData: undefined,
  products: [],
  productSelectionsByAreaAndPriceSystem: {},
  scheduleDelivery: false,
  isNewClient: false,
  isNewAddress: false,
  paymentTerm: 'Inmediato',
}

// Define all possible steps
const ALL_STEPS = [
  {
    name: 'Proveedor',
    component: ClientInfoStep,
    nextTriggerId: 'client-info-next',
    backTriggerId: 'client-info-back',
    isOptional: false,
  },
  {
    name: 'Detalles',
    component: DocumentDetailsStep,
    nextTriggerId: 'document-details-next',
    backTriggerId: 'document-details-back',
    isOptional: false,
  },
  {
    name: 'Productos',
    component: ProductSelectionStep,
    nextTriggerId: 'product-selection-next',
    backTriggerId: 'product-selection-back',
    isOptional: false,
  },
  {
    name: 'Entrega',
    component: DeliveryDetailsStep,
    nextTriggerId: 'delivery-details-next',
    backTriggerId: 'delivery-details-back',
    isOptional: true, // Mark as optional conceptually
  },
  {
    name: 'Revisión',
    component: ReviewStep,
    nextTriggerId: 'review-step-next',
    backTriggerId: 'review-step-back',
    isOptional: false,
  },
] as const

export function NewPurchaseOrderWizard({
  _open,
  onOpenChange,
  documentType,
  _invoiceId,
}: NewPurchaseOrderWizardProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [formData, setFormData] = useState<BillingFormData>({
    ...initialFormData,
    documentType,
  })
  const [requiresDeliveryStep, setRequiresDeliveryStep] = useState(false)
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false)
  const [isMainButtonDisabled, setIsMainButtonDisabled] = useState(false)

  // Use the static list of all steps
  const steps = ALL_STEPS

  // Handle delivery step toggle from ProductSelectionStep
  const handleToggleDelivery = (scheduleDelivery: boolean) => {
    // Direct state update, no animation state needed here
    setRequiresDeliveryStep(scheduleDelivery)

    // If we are past the delivery step index (index 3) and it's being hidden,
    // we might need to adjust the current step to stay on a visible step.
    // Let's adjust only if we are on the Review step (last step).
    if (!scheduleDelivery && currentStepIndex === steps.length - 1) {
      // Find the previous visible step index
      let previousVisibleIndex = -1
      for (let i = currentStepIndex - 1; i >= 0; i--) {
        if (!(steps[i].name === 'Entrega' && !scheduleDelivery)) {
          previousVisibleIndex = i
          break
        }
      }
      if (previousVisibleIndex !== -1) {
        setCurrentStepIndex(previousVisibleIndex)
      }
    }
    // If adding the step and we were on the review step, we don't need to move,
    // as the review step just shifts visually.
  }

  const triggerStepSubmit = () => {
    const triggerId = steps[currentStepIndex]?.nextTriggerId
    if (triggerId) {
      const button = document.getElementById(triggerId) as HTMLButtonElement
      if (button) {
        // Check if the hidden button is disabled
        if (button.disabled) {
          return
        }
        button.click()
      } else {
        // Check if it's the logical end step (Review)
        if (currentStepIndex === steps.length - 1) {
          handleFinalSubmit(formData)
        }
      }
    }
  }

  // Find the actual last visible step index for button text logic
  const lastVisibleStepIndex = useMemo(() => {
    for (let i = steps.length - 1; i >= 0; i--) {
      if (!(steps[i].name === 'Entrega' && !requiresDeliveryStep)) {
        return i
      }
    }
    return -1 // Should not happen in practice
  }, [requiresDeliveryStep, steps])

  const handleStepCompletion = (stepData: Partial<BillingFormData>) => {
    // Reset main button state when step completes successfully
    setIsMainButtonDisabled(false)

    // Calculate derived data (like calculatedDueDate) before setting state
    const derivedData: Partial<BillingFormData> = {}
    const paymentTermChanged = stepData.paymentTerm !== undefined
    const currentPaymentTerm = stepData.paymentTerm ?? formData.paymentTerm

    // Calculate due date only if paymentTerm is being set/changed in this step (index 1)
    if (currentStepIndex === 1 && paymentTermChanged) {
      if (
        currentPaymentTerm &&
        currentPaymentTerm !== 'custom' &&
        currentPaymentTerm !== 'immediate'
      ) {
        try {
          const days = parseInt(currentPaymentTerm.replace('days', ''))
          if (!isNaN(days)) {
            const date = new Date()
            date.setDate(date.getDate() + days)
            derivedData.calculatedDueDate = date
          } else {
            derivedData.calculatedDueDate = null
          }
        } catch (_e) {
          derivedData.calculatedDueDate = null
        }
      } else {
        // If term is custom, immediate, or invalid, clear the calculated date
        derivedData.calculatedDueDate = null
      }
    }

    // Update requiresDeliveryStep state specifically from Product step data
    if (currentStepIndex === 2 && stepData.scheduleDelivery !== undefined) {
      setRequiresDeliveryStep(stepData.scheduleDelivery)
    }

    const updatedData = { ...formData, ...stepData, ...derivedData }
    setFormData(updatedData)

    // Check if the current step is the last VISIBLE step
    if (currentStepIndex === lastVisibleStepIndex) {
      handleFinalSubmit(updatedData)
      return
    }

    // Find the next visible step index
    let nextVisibleIndex = -1
    for (let i = currentStepIndex + 1; i < steps.length; i++) {
      const isNextStepHidden =
        steps[i].name === 'Entrega' && !requiresDeliveryStep
      if (!isNextStepHidden) {
        nextVisibleIndex = i
        break
      }
    }

    // Move to the next visible step
    if (nextVisibleIndex !== -1) {
      setCurrentStepIndex(nextVisibleIndex)
    } else {
      // Should theoretically not happen if lastVisibleStepIndex logic is correct
      // No action needed
    }
  }

  // Handle back navigation with data saving
  const handleBackStep = (stepData: Partial<BillingFormData>) => {
    // Update requiresDeliveryStep state specifically from Product step data
    if (currentStepIndex === 2 && stepData.scheduleDelivery !== undefined) {
      setRequiresDeliveryStep(stepData.scheduleDelivery)
    }

    const updatedData = { ...formData, ...stepData }
    setFormData(updatedData)
  }

  const handleFinalSubmit = async (_finalData: BillingFormData) => {
    try {
      // Show loading toast message
      const loadingMessage = 'Creando orden de compra...'
      toast.info(loadingMessage, {
        toastId: 'creating-purchase-order',
        autoClose: false,
      })

      // UX Improvement: Close dialog and show success message
      handleClose()

      toast.success('Orden de compra creada exitosamente')
    } catch (_error) {
      toast.error('Error al crear la orden de compra')
    }
  }

  const handlePrevious = () => {
    // Trigger the back button of the current step to save data
    const currentStep = steps[currentStepIndex]
    if (currentStep?.backTriggerId) {
      const backButton = document.getElementById(
        currentStep.backTriggerId
      ) as HTMLButtonElement
      if (backButton) {
        backButton.click()
      }
    }

    // Find the previous visible step index
    let previousVisibleIndex = -1
    for (let i = currentStepIndex - 1; i >= 0; i--) {
      const isPrevStepHidden =
        steps[i].name === 'Entrega' && !requiresDeliveryStep
      if (!isPrevStepHidden) {
        previousVisibleIndex = i
        break
      }
    }
    if (previousVisibleIndex !== -1) {
      setCurrentStepIndex(previousVisibleIndex)
      // Reset button state when navigating back - let the target step handle its own validation
      setIsMainButtonDisabled(false)
    }
  }

  const handleStepClick = (index: number) => {
    // Only allow clicking on completed steps (index < currentStepIndex)
    // and ensure the clicked step is not hidden
    const isClickedStepHidden =
      steps[index].name === 'Entrega' && !requiresDeliveryStep
    if (index < currentStepIndex && !isClickedStepHidden) {
      // Save current step data before navigation
      const currentStep = steps[currentStepIndex]
      if (currentStep?.backTriggerId) {
        const backButton = document.getElementById(
          currentStep.backTriggerId
        ) as HTMLButtonElement
        if (backButton) {
          // Trigger the back button to save current step data
          backButton.click()
        }
      }

      // Navigate to the target step
      setCurrentStepIndex(index)
      // Reset button state when navigating - let the target step handle its own validation
      setIsMainButtonDisabled(false)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    setTimeout(() => {
      setCurrentStepIndex(0)
      setFormData({ ...initialFormData, documentType }) // Reset to the defined initial state
      setRequiresDeliveryStep(false) // Reset delivery requirement
    }, 300) // Delay matches dialog close animation
  }

  const handleCancelClick = () => {
    // If form has been modified, show confirmation dialog
    if (
      currentStepIndex > 0 ||
      JSON.stringify(formData) !==
        JSON.stringify({ ...initialFormData, documentType })
    ) {
      setShowCancelConfirmation(true)
    } else {
      // If no changes, just close directly
      handleClose()
    }
  }

  const CurrentStepComponent = steps[currentStepIndex]?.component
  const stepProps = {
    formData: formData,
    onNext: handleStepCompletion,
    onBack: handleBackStep, // Pass back handler to ALL steps for data preservation
    setIsMainButtonDisabled: setIsMainButtonDisabled, // Pass the setter function
    // Pass toggle handler only to the Product Selection step (index 2)
    ...(currentStepIndex === 2 && {
      onToggleDelivery: handleToggleDelivery,
    }),
  }

  if (!documentType) return null

  // Determine button text based on whether the *next* visible step is the last one
  let isNextStepTheLastVisible = false
  if (currentStepIndex < lastVisibleStepIndex) {
    let nextVisibleIdx = -1
    for (let i = currentStepIndex + 1; i < steps.length; i++) {
      if (!(steps[i].name === 'Entrega' && !requiresDeliveryStep)) {
        nextVisibleIdx = i
        break
      }
    }
    isNextStepTheLastVisible = nextVisibleIdx === lastVisibleStepIndex
  }

  const isCurrentStepTheLastVisible = currentStepIndex === lastVisibleStepIndex

  return (
    <>
      <Dialog
        onOpenChange={(isOpen) => {
          if (isOpen === false) {
            // Same check as in handleCancelClick
            if (
              currentStepIndex > 0 ||
              JSON.stringify(formData) !== JSON.stringify(initialFormData)
            ) {
              setShowCancelConfirmation(true)
            } else {
              handleClose()
            }
          } else {
            onOpenChange(true)
          }
        }}
        open={_open}
      >
        <DialogContent className="flex max-h-[90vh] flex-col overflow-y-auto sm:max-w-2xl md:max-w-3xl lg:max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-xl md:text-2xl">
              Crear Nueva Orden de Compra
            </DialogTitle>
            <DialogDescription>
              Al registrar una orden de compra, se gestionará la solicitud de
              productos a proveedores.
            </DialogDescription>
          </DialogHeader>

          {/* Simplified Stepper container */}
          <div className="px-4 py-4 sm:px-6">
            <Stepper
              activeStep={currentStepIndex}
              onStepClick={handleStepClick}
            >
              {steps.map((step, i) => (
                <Step
                  optional={step.isOptional}
                  key={`${step.name}-${i}`}
                  // Control visibility based on state and step name
                  isHidden={step.name === 'Entrega' && !requiresDeliveryStep}
                >
                  {step.name}
                </Step>
              ))}
            </Stepper>
          </div>

          <div className="min-h-[350px] flex-grow overflow-y-auto px-4 py-4 sm:px-6">
            {CurrentStepComponent && <CurrentStepComponent {...stepProps} />}
          </div>

          <DialogFooter className="flex flex-col-reverse gap-2 border-t px-4 pt-4 sm:flex-row sm:justify-between sm:px-6">
            <Button onClick={handleCancelClick} variant="outline">
              Cancelar
            </Button>
            <div className="flex gap-2">
              {currentStepIndex > 0 && (
                <Button onClick={handlePrevious} variant="outline">
                  Atrás
                </Button>
              )}
              <Button
                disabled={isMainButtonDisabled}
                onClick={triggerStepSubmit}
              >
                {isCurrentStepTheLastVisible
                  ? 'Crear Orden de Compra'
                  : isNextStepTheLastVisible
                    ? 'Revisar'
                    : 'Siguiente'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog
        onOpenChange={setShowCancelConfirmation}
        open={showCancelConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar el proceso?</AlertDialogTitle>
            <AlertDialogDescription>
              Si cancela ahora, perderá todos los datos ingresados. ¿Está seguro
              de que desea cancelar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction onClick={handleClose}>
              Sí, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// Add default export as well for flexibility
export default NewPurchaseOrderWizard
